<script setup lang="ts">
import { nextTick, useTemplateRef } from "vue";

import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useTeamSelector } from "@/composables/useTeamSelector";
import type { Icon } from "@/model/icon";
import type { Art, Team } from "@/model/session";

const props = defineProps<{
  selected?: Team;
  title?: string;
  showCurrentTeam?: boolean;
  showClear?: boolean;
  showOtherArts?: boolean;
  showExternal?: boolean;
}>();

const emit = defineEmits<{ select: [Team | null] }>();

const {
  view,
  selectedArt,
  searchVal,
  teamsList,
  externalsList,
  artsList,
  showExternalsFolder,
  showArtNav,
  resultsCount,
  searchPlaceholder,
} = useTeamSelector(props);

const artListItemRefs = useTemplateRef("artListItems");
const otherArtsButtonRef = useTemplateRef("otherArtsButton");
const externalsButtonRef = useTemplateRef("externalsButton");
const externalListItemRefs = useTemplateRef("externalListItems");

/**
 * Emit the selected team
 */
const selectTeam = (team: Team | null) => emit("select", team);

/**
 * Switch view, reset the search, then move focus (resolved after the DOM updates).
 */
const switchView = async (target: typeof view.value, focus: () => void) => {
  view.value = target;
  searchVal.value = "";

  await nextTick();
  focus();
};

/** Show teams for the selected ART */
const selectArt = (art: Art) => {
  selectedArt.value = art;
  return switchView("teams", () => otherArtsButtonRef.value?.focus());
};

/** Show all ARTs the user can choose from */
const showArts = () => switchView("arts", () => artListItemRefs.value?.[0]?.focus());

/** Show the external orgs of the current ART */
const showExternals = () => switchView("externals", () => externalListItemRefs.value?.[0]?.focus());

/** Return from the external orgs folder to the team list */
const showTeams = () => switchView("teams", () => externalsButtonRef.value?.focus());

function teamIcon(team: Team): Icon {
  return team.external ? "persona/external-team" : "persona/team";
}
</script>

<template>
  <MenuList selectable>
    <MenuListHeader>
      <MenuListTitle>{{ title || $t("action.team") }}</MenuListTitle>
      <MenuListSearch
        :key="view"
        :placeholder="searchPlaceholder"
        :icon-title="$t('label.teamSelector.searchTitle')"
        :results-count
        @search="searchVal = $event"
      />
    </MenuListHeader>
    <MenuListItems>
      <!-- Teams view -->
      <template v-if="view === 'teams'">
        <MenuListItem
          v-if="showArtNav && !searchVal"
          ref="otherArtsButton"
          @click.stop="showArts()"
        >
          <template #before>
            <SvgIcon name="arrow/chevron-left" width="16" height="16" />
          </template>
          {{ $t("programBoard.otherArts") }}
        </MenuListItem>

        <!-- 'Clear' button ("No Team") -->
        <MenuListItem v-if="showClear && selected" @click.stop="selectTeam(null)">
          <template #before>
            <SvgIcon name="persona/no-user" width="20" height="20" />
          </template>
          {{ $t("general.unassigned") }}
        </MenuListItem>

        <!-- Teams (currently-selected team is active+static)-->
        <MenuListItem
          v-for="team in teamsList"
          :key="team.id"
          :selected="team.id === selected?.id"
          :static="team.id === selected?.id"
          @click.stop="selectTeam(team)"
        >
          <template #before>
            <SvgIcon :name="teamIcon(team)" width="20" height="20" />
          </template>
          {{ team.name }}
        </MenuListItem>

        <MenuListItem v-if="!teamsList?.length && !showExternalsFolder" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>

        <!-- 'External orgs' folder (current ART only, hidden while searching) -->
        <MenuListItem
          v-if="showExternalsFolder"
          ref="externalsButton"
          @click.stop="showExternals()"
        >
          <template #before>
            <SvgIcon name="persona/external-team" width="20" height="20" />
          </template>
          {{ $t("label.teamSelector.externalOrgs") }}
          <template #after>
            <SvgIcon name="arrow/chevron-right" width="16" height="16" />
          </template>
        </MenuListItem>
      </template>

      <!-- External orgs of the current ART -->
      <template v-else-if="view === 'externals'">
        <MenuListItem @click.stop="showTeams()">
          <template #before>
            <SvgIcon name="arrow/chevron-left" width="16" height="16" />
          </template>
          {{ $t("label.teamSelector.teams") }}
        </MenuListItem>

        <MenuListItem
          v-for="team in externalsList"
          :key="team.id"
          ref="externalListItems"
          :selected="team.id === selected?.id"
          :static="team.id === selected?.id"
          @click.stop="selectTeam(team)"
        >
          <template #before>
            <SvgIcon name="persona/external-team" width="20" height="20" />
          </template>
          {{ team.name }}
        </MenuListItem>

        <MenuListItem v-if="!externalsList?.length" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </template>

      <!-- List of ARTs -->
      <template v-else>
        <MenuListItem
          v-for="art in artsList"
          :key="art.id"
          ref="artListItems"
          @click.stop="selectArt(art)"
        >
          <template #before>
            <SvgIcon name="general/art" width="20" height="20" />
          </template>
          {{ art.name }}
          <template #after>
            <SvgIcon name="arrow/chevron-right" width="16" height="16" />
          </template>
        </MenuListItem>

        <MenuListItem v-if="!artsList?.length" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </template>
    </MenuListItems>
  </MenuList>
</template>
